/** Tiny helpers for drawing pixel art procedurally onto offscreen canvases. */
import { cleanSprite } from './truepx/clean';
import { truePxFigures } from './truepx/flags';
import { addDetail, type Detail } from './detail';

const ANIMATION_FRAMES = new WeakSet<object>();
export const markAnimationFrame = (image: object): void => {
  ANIMATION_FRAMES.add(image);
};
export const isAnimationFrame = (image: object): boolean => ANIMATION_FRAMES.has(image);

const REVISIONS = new WeakMap<object, number>();
/** Notify texture consumers after an already-published procedural canvas changes. */
export function touchCanvas(c: object): void {
  REVISIONS.set(c, (REVISIONS.get(c) ?? 0) + 1);
}
export const canvasRevision = (c: object): number => REVISIONS.get(c) ?? 0;

export function canvas(w: number, h: number): HTMLCanvasElement {
  // In a worker (figures are drawn off the main thread) there is no document: an offscreen canvas
  // has the same 2D drawing context.
  if (typeof document === 'undefined') return new OffscreenCanvas(w, h) as unknown as HTMLCanvasElement;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

const RES = new WeakMap<HTMLCanvasElement, { res: number; x: number; y: number }>();
/**
 * Marks a frame drawn at `res` canvas pixels per art pixel (rig figures are drawn at 2), cut from
 * a full frame at art pixel (`x`, `y`) (see `cropFrame`).
 */
export function atRes(c: HTMLCanvasElement, res: number, x = 0, y = 0): HTMLCanvasElement {
  if (res !== 1 || x || y) RES.set(c, { res, x, y });
  return c;
}
/** Canvas pixels per art pixel of a frame: 1 unless it was marked. */
export const resOf = (c: HTMLCanvasElement): number => RES.get(c)?.res ?? 1;
/** Where a cropped frame's top-left sits in the full frame, in art pixels (0 if uncropped). */
export const artX = (c: HTMLCanvasElement): number => RES.get(c)?.x ?? 0;
export const artY = (c: HTMLCanvasElement): number => RES.get(c)?.y ?? 0;

const FINISHED = new WeakMap<HTMLCanvasElement, Map<string, HTMLCanvasElement>>();
/**
 * Scenery drawn at one pixel per art pixel, finished for the world: its detail pass and, when
 * `lit`, a rim of key light along its edges. Everything shares the buildings' grid, so nothing
 * is enlarged. Cached: the same tree or rock is finished once however often it is placed.
 */
export function finishArt(c: HTMLCanvasElement, lit = false, detail?: Detail): HTMLCanvasElement {
  if (resOf(c) !== 1 || (!lit && !detail)) return c;
  const way = `${lit ? 1 : 0}${detail ?? ''}`;
  let ways = FINISHED.get(c);
  if (!ways) FINISHED.set(c, (ways = new Map()));
  const done = ways.get(way);
  if (done) return done;
  const { width: W, height: H } = c;
  const out = canvas(W, H);
  const g = ctx2d(out);
  const img = ctx2d(c).getImageData(0, 0, W, H);
  const o = new Uint32Array(img.data.buffer);
  if (detail) addDetail(o, W, H, detail);
  if (lit) rim(o, W, H);
  g.putImageData(img, 0, 0);
  atRes(out, 1, artX(c), artY(c));
  ways.set(way, out);
  return out;
}

/**
 * Light along the edges of a sprite, one pixel wide: brighter where it
 * faces the key light (up and to the right, as on the figures), darker on the side away from it.
 */
function rim(o: Uint32Array, W: number, H: number): void {
  const src = o.slice();
  const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && src[y * W + x]! >>> 24 > 0;
  const tint = (px: number, k: number) => {
    // Little-endian RGBA: red in the low byte.
    const r = px & 255;
    const g = (px >>> 8) & 255;
    const b = (px >>> 16) & 255;
    const f = (v: number, warm: number) =>
      Math.max(0, Math.min(255, Math.round(k > 0 ? v + (warm - v) * k : v * (1 + k))));
    return (px & 0xff000000) | (f(b, 205) << 16) | (f(g, 240) << 8) | f(r, 255);
  };
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const px = src[y * W + x]!;
      if (!(px >>> 24)) continue;
      const lit = !solid(x + 1, y) || !solid(x, y - 1);
      const dark = !solid(x - 1, y) || !solid(x, y + 1);
      if (lit && !dark) o[y * W + x] = tint(px, 0.22);
      else if (dark && !lit) o[y * W + x] = tint(px, -0.2);
    }
}

/**
 * A sprite at a coarser grain than it was drawn: every `k`×`k` block one colour (solid where most
 * of it is, in the colour most of its solid pixels share), at the same size and place. Blocks run from the sprite's bottom-left, so
 * whatever stands on the ground keeps its footing.
 */
export function coarsen(c: HTMLCanvasElement, k: number): HTMLCanvasElement {
  if (k <= 1) return c;
  const { width: W, height: H } = c;
  const src = new Uint32Array(ctx2d(c).getImageData(0, 0, W, H).data.buffer);
  const out = canvas(W, H);
  const g = ctx2d(out);
  const img = g.createImageData(W, H);
  const o = new Uint32Array(img.data.buffer);
  const top = H - Math.ceil(H / k) * k;
  for (let y0 = top; y0 < H; y0 += k)
    for (let x0 = 0; x0 < W; x0 += k) {
      let n = 0;
      let cells = 0;
      let red = 0;
      let green = 0;
      let blue = 0;
      let alpha = 0;
      for (let y = Math.max(0, y0); y < y0 + k; y++)
        for (let x = x0; x < Math.min(W, x0 + k); x++) {
          cells++;
          const px = src[y * W + x]!;
          const a = px >>> 24;
          if (a < 128) continue;
          n++;
          // Little-endian RGBA: red in the low byte.
          red += px & 255;
          green += (px >>> 8) & 255;
          blue += (px >>> 16) & 255;
          alpha += a;
        }
      if (!n || n * 2 < cells) continue;
      const v =
        ((Math.round(alpha / n) << 24) |
          (Math.round(blue / n) << 16) |
          (Math.round(green / n) << 8) |
          Math.round(red / n)) >>>
        0;
      for (let y = Math.max(0, y0); y < y0 + k; y++)
        for (let x = x0; x < Math.min(W, x0 + k); x++) o[y * W + x] = v;
    }
  g.putImageData(img, 0, 0);
  return atRes(out, resOf(c), artX(c), artY(c));
}

/**
 * A frame cut down to what is drawn on it, plus a pixel of margin: most of a figure's frame is
 * empty room for weapons and capes, and a crowd keeps thousands of frames. It remembers where it
 * was cut so it is drawn where the full frame would have been (see `artX`).
 */
export function cropFrame(c: HTMLCanvasElement): HTMLCanvasElement {
  const res = resOf(c);
  const { width: W, height: H } = c;
  const d = new Uint32Array(ctx2d(c).getImageData(0, 0, W, H).data.buffer);
  let x0 = W;
  let y0 = H;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++)
      if (d[y * W + x]! >>> 24) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
  if (x1 < 0) return atRes(canvas(res, res), res);
  // Whole art pixels, so a cropped frame lands on the same pixel grid as a full one.
  x0 = Math.max(0, Math.floor(x0 / res) * res - res);
  y0 = Math.max(0, Math.floor(y0 / res) * res - res);
  x1 = Math.min(W, Math.ceil((x1 + 1) / res) * res + res);
  y1 = Math.min(H, Math.ceil((y1 + 1) / res) * res + res);
  const out = canvas(x1 - x0, y1 - y0);
  ctx2d(out).drawImage(c, -x0, -y0);
  return atRes(out, res, artX(c) + x0 / res, artY(c) + y0 / res);
}
/** A frame's width and height in art pixels, whatever resolution it was drawn at. */
export const artW = (c: HTMLCanvasElement): number => c.width / resOf(c);
export const artH = (c: HTMLCanvasElement): number => c.height / resOf(c);

export function ctx2d(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const g = c.getContext('2d', { willReadFrequently: true });
  if (!g) throw new Error('Canvas 2D is not available');
  return g;
}

export function box(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  g.fillStyle = color;
  g.fillRect(x, y, w, h);
}

/** Filled pixel ellipse built from horizontal spans, so edges stay crisp. */
export function ell(
  g: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  color: string,
): void {
  g.fillStyle = color;
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    const dy = (y + 0.5 - cy) / ry;
    if (dy < -1 || dy > 1) continue;
    const hw = rx * Math.sqrt(1 - dy * dy);
    const x0 = Math.round(cx - hw);
    const x1 = Math.round(cx + hw);
    if (x1 > x0) g.fillRect(x0, y, x1 - x0, 1);
  }
}

/** Bresenham line, one pixel wide. */
export function line(
  g: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  color: string,
): void {
  g.fillStyle = color;
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (let n = 0; n < 256; n++) {
    g.fillRect(x0, y0, 1, 1);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x0 += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y0 += sy;
    }
  }
}

export const hexRgb = (h: string): [number, number, number] => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];

/** Adds a 1px dark outline around every opaque pixel. */
export function outline(c: HTMLCanvasElement, color = '#18121e'): HTMLCanvasElement {
  const g = ctx2d(c);
  const { width: w, height: h } = c;
  const img = g.getImageData(0, 0, w, h);
  const s = img.data;
  const o = new Uint8ClampedArray(s);
  const [r, gg, b] = hexRgb(color);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (s[i + 3]) continue;
      const near =
        (x > 0 && s[i - 1]) ||
        (x < w - 1 && s[i + 7]) ||
        (y > 0 && s[i - w * 4 + 3]) ||
        (y < h - 1 && s[i + w * 4 + 3]);
      if (near) {
        o[i] = r;
        o[i + 1] = gg;
        o[i + 2] = b;
        o[i + 3] = 255;
      }
    }
  img.data.set(o);
  g.putImageData(img, 0, 0);
  return c;
}

/**
 * Selective outline ("sel-out"): like `outline`, but each outline pixel takes a deep shade of the
 * colour it borders instead of one flat ink, so a blue robe is edged in dark navy, skin in dark
 * brown and gold in deep bronze. Edges facing the key light (up and to the right) stay a little
 * lighter than edges in shadow, and everything is pulled toward `ink` so the figure still reads
 * crisply against any ground.
 */
export function selOutline(c: HTMLCanvasElement, ink = '#0c0a12'): HTMLCanvasElement {
  const g = ctx2d(c);
  const { width: w, height: h } = c;
  const img = g.getImageData(0, 0, w, h);
  const s = img.data;
  const o = new Uint8ClampedArray(s);
  const [ir, ig, ib] = hexRgb(ink);
  let r = 0;
  let gg = 0;
  let b = 0;
  let n = 0;
  /** Takes in the bordered pixel at byte `j`, keeping `keep` of its colour. */
  const add = (j: number, keep: number) => {
    if (!s[j + 3]) return;
    // Cool the bordered colour a touch on its way down, as the hand-picked shadows do.
    r += ir + (s[j]! - ir) * keep;
    gg += ig + (s[j + 1]! - ig) * keep;
    b += ib + (s[j + 2]! - ib) * keep * 1.08;
    n++;
  };
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (s[i + 3]) continue;
      r = gg = b = n = 0;
      // How much of the bordered colour survives: lit sides keep more. Bordered pixel to the left
      // (the outline is on the figure's right, lit side), below (on top, lit), to the right (on
      // the left, shadow side) and above (underneath, shadow side).
      if (x > 0) add(i - 4, 0.3);
      if (y < h - 1) add(i + w * 4, 0.3);
      if (x < w - 1) add(i + 4, 0.17);
      if (y > 0) add(i - w * 4, 0.14);
      if (!n) continue;
      o[i] = r / n;
      o[i + 1] = gg / n;
      o[i + 2] = b / n;
      o[i + 3] = 255;
    }
  img.data.set(o);
  g.putImageData(img, 0, 0);
  return c;
}

/**
 * Form lighting for a finished figure (before outlining), with the key light up and to the right:
 * a warm rim on right and top edges, cool shadow on undersides and the far edge, and a soft
 * darkening toward the feet where the ground occludes light. Turns flat two-tone fills into
 * three-tone volumes without per-part shading code.
 */
export function litShade(c: HTMLCanvasElement, R = 1): HTMLCanvasElement {
  const g = ctx2d(c);
  const { width: w, height: h } = c;
  const img = g.getImageData(0, 0, w, h);
  const s = img.data;
  const a = new Uint8Array(w * h);
  let top = h;
  let bot = 0;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++)
      if (s[(y * w + x) * 4 + 3]) {
        a[y * w + x] = 1;
        top = Math.min(top, y);
        bot = Math.max(bot, y);
      }
  const on = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && a[y * w + x] === 1;
  const foot = bot - (bot - top) * 0.22;
  for (let y = 0; y < h; y++) {
    // Each horizontal run of the figure is shaded like a cylinder: dark at its left, lit at its right.
    let run0 = 0;
    let run1 = -1;
    for (let x = 0; x < w; x++) {
      if (!a[y * w + x]) continue;
      if (x > run1) {
        run0 = x;
        run1 = x;
        while (run1 + 1 < w && a[y * w + run1 + 1]) run1++;
      }
      const span = run1 - run0;
      let f = span >= 4 * R ? ((x - run0) / span - 0.55) * 0.2 : 0;
      // Edges one art pixel (`R` canvas pixels) deep, measured on the art grid.
      const ax = x - (x % R);
      const ay = y - (y % R);
      if (!on(ax + R, y)) f += 0.3;
      else if (!on(ax + 2 * R, y)) f += 0.07;
      if (!on(x, ay - 1)) f += 0.12;
      if (!on(ax - 1, y)) f -= 0.1;
      if (!on(x, ay + R)) f -= 0.14;
      if (y > foot) f -= (0.14 * (y - foot)) / (bot - foot || 1);
      // Snap to a few bands so the result stays a limited pixel-art palette, not a smooth gradient.
      f = Math.round(f / 0.08) * 0.08;
      if (!f) continue;
      const i = (y * w + x) * 4;
      if (f > 0) {
        s[i] = s[i]! + (255 - s[i]!) * f;
        s[i + 1] = s[i + 1]! + (244 - s[i + 1]!) * f;
        s[i + 2] = s[i + 2]! + (214 - s[i + 2]!) * f;
      } else {
        const k = 1 + f;
        s[i] = s[i]! * k;
        s[i + 1] = s[i + 1]! * k;
        s[i + 2] = s[i + 2]! * k + 10 * -f;
      }
    }
  }
  g.putImageData(img, 0, 0);
  return c;
}
/** Luma of a little-endian RGBA pixel. */
const luma = (p: number): number =>
  0.299 * (p & 255) + 0.587 * ((p >>> 8) & 255) + 0.114 * ((p >>> 16) & 255);
const solid = (p: number | undefined): p is number => p !== undefined && p >>> 24 >= 128;

/**
 * The option C line pass (density.ts) on a picture enlarged `k` times from art pixels: every run of
 * one colour `k` pixels long (a one-art-pixel line) that is darker than what lies beside it becomes
 * one pixel wide, the rest of the run taking the colour beside it. The pixel kept is the one on the
 * outside of a shape (next to bare canvas), else the one away from the light (left, below), so the
 * lines stay where the art put them. Across rows first (upright lines), then down columns.
 * Only lines at least four art pixels long are touched (small features — a mouth, a button — keep
 * their shape), and none of layer `skip` (the face's stamped features). `owner` and `depth`, when
 * given, follow the colours.
 */
export function thinLines(
  px: Uint32Array,
  W: number,
  H: number,
  k: number,
  owner?: Int16Array,
  depth?: Float32Array,
  skip = -2,
): void {
  const DARKER = 10;
  // Size of each pixel's patch of one colour (4-connected), to leave small features alone.
  const size = new Int32Array(W * H);
  const seen = new Uint8Array(W * H);
  const stack: number[] = [];
  const members: number[] = [];
  for (let s = 0; s < W * H; s++) {
    if (seen[s] || !solid(px[s])) continue;
    const col = px[s];
    stack.push(s);
    seen[s] = 1;
    members.length = 0;
    while (stack.length) {
      const i = stack.pop()!;
      members.push(i);
      const x = i % W;
      for (const j of [x > 0 ? i - 1 : -1, x < W - 1 ? i + 1 : -1, i - W, i + W])
        if (j >= 0 && j < W * H && !seen[j] && px[j] === col) {
          seen[j] = 1;
          stack.push(j);
        }
    }
    for (const i of members) size[i] = members.length;
  }
  const big = 4 * k * k;
  const take = (to: number, from: number) => {
    px[to] = px[from]!;
    if (owner) owner[to] = owner[from]!;
    if (depth) depth[to] = depth[from]!;
  };
  /** One line of `n` pixels from `i0`, `step` apart; `keepLast`: between two lit sides, keep the run's last pixel. */
  const pass = (i0: number, n: number, step: number, keepLast: boolean) => {
    let a = 0;
    while (a < n) {
      const p = px[i0 + a * step]!;
      let b = a + 1;
      while (b < n && px[i0 + b * step] === p) b++;
      const len = b - a;
      const i = i0 + a * step;
      if (solid(p) && len >= k && len < 2 * k && size[i]! >= big && !(owner && owner[i] === skip)) {
        const before = a > 0 ? i0 + (a - 1) * step : -1;
        const after = b < n ? i0 + b * step : -1;
        const A = before >= 0 ? px[before] : undefined;
        const B = after >= 0 ? px[after] : undefined;
        const L = luma(p);
        const litA = solid(A) && luma(A) > L + DARKER;
        const litB = solid(B) && luma(B) > L + DARKER;
        // The pixels of the run that stay: one for a one-art-pixel line.
        const keep = len - (k - 1);
        let first = -1;
        if (!solid(A) && litB) first = a + keep;
        else if (!solid(B) && litA) first = a;
        else if (litA && litB) first = keepLast ? a : a + keep;
        if (first >= 0) {
          const from = first === a ? before : after;
          for (let t = first; t < first + (len - keep); t++) take(i0 + t * step, from);
        }
      }
      a = b;
    }
  };
  for (let y = 0; y < H; y++) pass(y * W, W, 1, false);
  for (let x = 0; x < W; x++) pass(x, H, W, true);
}

const ON_FIGURE_GRID = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
/**
 * A sprite drawn at one canvas pixel per art pixel (a beast) brought onto the figure grid of option C
 * (density.ts): Scale2x, then its one-art-pixel dark lines thinned to one pixel (`thinLines`).
 * Cached; keeps where it was cut from (`artX`).
 */
export function toFigureGrid(c: HTMLCanvasElement): HTMLCanvasElement {
  if (resOf(c) !== 1) return c;
  const done = ON_FIGURE_GRID.get(c);
  if (done) return done;
  const { width: W, height: H } = c;
  const s = new Uint32Array(ctx2d(c).getImageData(0, 0, W, H).data.buffer);
  const out = canvas(W * 2, H * 2);
  const g = ctx2d(out);
  const img = g.createImageData(W * 2, H * 2);
  const o = new Uint32Array(img.data.buffer);
  const W2 = W * 2;
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const P = y * W + x;
      const a = s[y > 0 ? P - W : P]!;
      const b = s[x < W - 1 ? P + 1 : P]!;
      const cc = s[x > 0 ? P - 1 : P]!;
      const d = s[y < H - 1 ? P + W : P]!;
      const e = s[P]!;
      const j = y * 2 * W2 + x * 2;
      o[j] = cc === a && cc !== d && a !== b ? a : e;
      o[j + 1] = a === b && a !== cc && b !== d ? b : e;
      o[j + W2] = d === cc && d !== b && cc !== a ? cc : e;
      o[j + W2 + 1] = b === d && b !== a && d !== cc ? d : e;
    }
  thinLines(o, W2, H * 2, 2);
  // Lane C0-TRUEPX-GAME: beasts get the figures' true-pixel clean-up (render/truepx/) when the page draws figures so.
  if (truePxFigures()) cleanSprite(o, W2, H * 2, 1, true);
  g.putImageData(img, 0, 0);
  atRes(out, 2, artX(c), artY(c));
  ON_FIGURE_GRID.set(c, out);
  return out;
}

/**
 * A picture on the figure grid (two pixels per art pixel, `W`×`H`) brought down to one pixel per
 * art pixel, for the 1x mode and the 1× bake derived from the 2× one (density.ts):
 * - each 2×2 block becomes one pixel, solid when at least half of it is;
 * - in the colour most of its solid pixels share; on a tie the darkest, so a one-G2-pixel line or
 *   outline beats the fill beside it and the lines survive;
 * - the outline mended: a pixel on the edge of the shape whose block held part of the old edge takes
 *   the darkest colour of that edge, so a one-pixel outline stays closed;
 * - lone pixels (nothing solid beside them) dropped.
 * Returns the picture and its size (half, rounded up).
 */
export function halveFigure(
  src: Uint32Array,
  W: number,
  H: number,
): { px: Uint32Array; w: number; h: number } {
  const w = Math.ceil(W / 2);
  const h = Math.ceil(H / 2);
  const o = new Uint32Array(w * h);
  /** Per output pixel: the darkest pixel of its block on the old shape's edge (0 for none). */
  const edge = new Uint32Array(w * h);
  const on = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && src[y * W + x]! >>> 24 >= 128;
  const cols = [0, 0, 0, 0];
  const counts = [0, 0, 0, 0];
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      let n = 0;
      let kinds = 0;
      let rim = 0;
      for (let dy = 0; dy < 2; dy++)
        for (let dx = 0; dx < 2; dx++) {
          const sx = x * 2 + dx;
          const sy = y * 2 + dy;
          if (!on(sx, sy)) continue;
          const p = src[sy * W + sx]!;
          n++;
          let k = 0;
          while (k < kinds && cols[k] !== p) k++;
          if (k === kinds) {
            cols[kinds] = p;
            counts[kinds++] = 0;
          }
          counts[k]!++;
          if (
            (!on(sx - 1, sy) || !on(sx + 1, sy) || !on(sx, sy - 1) || !on(sx, sy + 1)) &&
            (!rim || luma(p) < luma(rim))
          )
            rim = p;
        }
      if (n < 2) continue;
      let best = 0;
      for (let k = 1; k < kinds; k++)
        if (counts[k]! > counts[best]! || (counts[k] === counts[best] && luma(cols[k]!) < luma(cols[best]!)))
          best = k;
      o[y * w + x] = cols[best]!;
      edge[y * w + x] = rim;
    }
  const solidAt = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && o[y * w + x]! >>> 24 >= 128;
  const out = o.slice();
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (!solidAt(x, y)) continue;
      const l = solidAt(x - 1, y);
      const r = solidAt(x + 1, y);
      const u = solidAt(x, y - 1);
      const d = solidAt(x, y + 1);
      if (!l && !r && !u && !d) out[i] = 0;
      else if ((!l || !r || !u || !d) && edge[i]) out[i] = edge[i]!;
    }
  return { px: out, w, h };
}

/**
 * A figure frame drawn on the figure grid brought down to one canvas pixel per art pixel
 * (`halveFigure`), keeping where it was cut from (`artX`). Anything not at two is returned as it is.
 */
export function toArtRes(c: HTMLCanvasElement): HTMLCanvasElement {
  if (resOf(c) !== 2) return c;
  const { width: W, height: H } = c;
  const src = new Uint32Array(ctx2d(c).getImageData(0, 0, W, H).data.buffer);
  const { px, w, h } = halveFigure(src, W, H);
  const out = canvas(w, h);
  const g = ctx2d(out);
  const img = g.createImageData(w, h);
  new Uint32Array(img.data.buffer).set(px);
  g.putImageData(img, 0, 0);
  return atRes(out, 1, artX(c), artY(c));
}
